/**
 * Demande d'accès restaurateur (« Je suis restaurateur » à l'inscription) :
 * modèle de données, limites et validation des champs saisis.
 *
 * Parcours : le demandeur crée son compte (profil `users/{uid}` en CLIENT, jamais
 * RESTAURATEUR) et une demande `restaurateurApplications/{uid}` en `pending` — ou,
 * déjà client, dépose sa demande avec son compte existant. Un admin la valide
 * (création du restaurant + passage en RESTAURATEUR, en une transaction) ou la
 * refuse avec un motif. Le demandeur peut retirer sa demande tant qu'elle est en attente.
 *
 * Miroir côté règles : `firestore.rules` (match /restaurateurApplications) —
 * limites et formats à modifier ensemble ; testé par tests/rules (npm run test:rules).
 * Le SIRET n'est contrôlé que par son format dans les règles : la clé de Luhn
 * est vérifiée ici, à la saisie (v2 : vérification auprès de l'INSEE, API Sirene).
 */

export type RestaurateurApplicationStatus = 'pending' | 'approved' | 'rejected';

/** Document `restaurateurApplications/{uid}` (identifiant = uid du demandeur). */
export interface RestaurateurApplication {
    /** uid Firebase Auth du demandeur (= identifiant du document). */
    id: string;
    firstName: string;
    lastName: string;
    /** E-mail du compte Firebase Auth (imposé par les règles). */
    email: string;
    /** Format international E.164 : « +33612345678 ». */
    phone: string;
    restaurantName: string;
    address: string;
    /** 14 chiffres, sans espaces. */
    siret: string;
    status: RestaurateurApplicationStatus;
    /**
     * E-mail confirmé (lien de vérification Firebase). Mis à jour par le demandeur
     * depuis l'écran d'attente, mais les règles imposent la valeur du jeton Firebase
     * Auth (`email_verified`) pour l'adresse de la demande : impossible à falsifier.
     * Obligatoire pour qu'un admin puisse valider la demande.
     */
    emailVerified: boolean;
    /** Version du texte accepté (case « J'accepte » / conditions). */
    termsVersion: string;
    termsAcceptedAt: string;
    createdAt: string;
    // ── Décision (admin uniquement) ──
    restaurantId?: string;
    decidedAt?: string;
    /** uid de l'admin qui a validé ou refusé. */
    decidedBy?: string;
    rejectionReason?: string;
    /** Demande refusée : date de suppression automatique (politique TTL Firestore). */
    purgeAt?: string;
}

/** Champs saisis dans le formulaire « Je suis restaurateur » (hors mot de passe). */
export interface RestaurateurApplicationInput {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    restaurantName: string;
    address: string;
    siret: string;
}

export interface RestaurateurSignupForm extends RestaurateurApplicationInput {
    password: string;
    acceptedTerms: boolean;
}

export type RestaurateurSignupField = keyof RestaurateurSignupForm;

/**
 * - `signup` : création du compte (e-mail + mot de passe) et de la demande ;
 * - `existingAccount` : client déjà connecté, la demande utilise son compte
 *   (pas de champ e-mail ni mot de passe).
 */
export type RestaurateurSignupMode = 'signup' | 'existingAccount';

/**
 * Ordre d'affichage des champs dans le formulaire : sert à placer le focus sur
 * le premier champ en erreur.
 */
export const RESTAURATEUR_SIGNUP_FIELDS: readonly RestaurateurSignupField[] = [
    'firstName', 'lastName', 'email', 'password', 'phone',
    'restaurantName', 'address', 'siret', 'acceptedTerms',
];

/** Longueurs (min, max) en caractères — miroir de firestore.rules. */
export const APPLICATION_LIMITS = {
    firstName: { min: 1, max: 80 },
    lastName: { min: 1, max: 80 },
    email: { min: 3, max: 254 },
    restaurantName: { min: 2, max: 120 },
    address: { min: 5, max: 300 },
    rejectionReason: { min: 3, max: 500 },
} as const;

/** Longueur minimale du mot de passe (Firebase en exige 6 ; on aligne sur les formulaires admin). */
export const MIN_PASSWORD_LENGTH = 8;

// ─── À renseigner par le propriétaire du produit ──────────────────────────

/**
 * Conditions d'utilisation professionnelles. Tant qu'elles valent null, aucun
 * lien n'est affiché et la case ne fait pas référence à un document inexistant.
 */
export const RESTAURATEUR_TERMS_URL: string | null = null;

/**
 * Version du texte accepté par le demandeur, enregistrée avec la demande
 * (`termsVersion`). À changer à chaque nouvelle formulation de la case ou des
 * conditions — par exemple « cgu-pro-2026-11 » quand RESTAURATEUR_TERMS_URL sera
 * renseignée. Format : lettres, chiffres, « . », « _ », « - » (miroir des règles).
 */
export const RESTAURATEUR_TERMS_VERSION = 'attestation-2026-10';

/** Adresse de contact (questions, droits d'accès et de suppression). null : pas encore connue. */
export const RESTAURATEUR_CONTACT_EMAIL: string | null = 'info@atthetable.fr';

/**
 * Conservation d'une demande refusée après la décision (champ `purgeAt`, supprimé
 * automatiquement par une politique TTL Firestore). Durée à valider par le
 * propriétaire du produit ; une demande validée est conservée avec le compte.
 */
export const REJECTED_APPLICATION_RETENTION_DAYS = 365;
export const REJECTED_APPLICATION_RETENTION_LABEL = '12 mois';

/** SIREN de La Poste : ses établissements ne respectent pas la clé de Luhn. */
export const LA_POSTE_SIREN = '356000000';

export type FieldCheck = { ok: true; value: string } | { ok: false; error: string };

// ─── Texte libre ───────────────────────────────────────────────────────────

/**
 * Retire les caractères de contrôle et de mise en forme invisibles (catégorie
 * Unicode C : inversion bidirectionnelle U+202E, espaces de largeur nulle…)
 * qui permettraient de maquiller un nom ou une adresse dans le panneau admin.
 * Les règles Firestore les refusent aussi.
 */
export function stripInvisibleChars(raw: string, { keepNewlines = false } = {}): string {
    return keepNewlines
        ? raw.replace(/\r\n?|[\u2028\u2029]/g, '\n').replace(/(?!\n)\p{C}/gu, '')
        : raw.replace(/\s+/g, ' ').replace(/\p{C}/gu, '');
}

// ─── SIRET ─────────────────────────────────────────────────────────────────

/** Retire espaces (y compris insécables), points et tirets : « 123.456.789-00012 » → « 12345678900012 ». */
export function compactSiret(raw: string): string {
    return raw.replace(/[\s  .\-]/g, '');
}

/** Clé de Luhn sur une chaîne de chiffres. */
function luhnValid(digits: string): boolean {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        let d = digits.charCodeAt(i) - 48;
        // Un chiffre sur deux en partant de la droite (le dernier n'est pas doublé).
        if ((digits.length - i) % 2 === 0) {
            d *= 2;
            if (d > 9) d -= 9;
        }
        sum += d;
    }
    return sum % 10 === 0;
}

/**
 * SIRET valide : 14 chiffres, SIREN non nul et clé de Luhn.
 * Exception La Poste (SIREN 356000000) : ses établissements sont valides si la
 * somme de leurs chiffres est un multiple de 5 (le siège, 35600000000048,
 * respecte la clé de Luhn).
 */
export function isValidSiret(siret: string): boolean {
    if (!/^\d{14}$/.test(siret)) return false;
    if (siret.startsWith('000000000')) return false; // SIREN nul : la clé de Luhn passerait
    if (luhnValid(siret)) return true;
    if (siret.startsWith(LA_POSTE_SIREN)) {
        let sum = 0;
        for (const c of siret) sum += c.charCodeAt(0) - 48;
        return sum % 5 === 0;
    }
    return false;
}

export function validateSiret(raw: string): FieldCheck {
    const siret = compactSiret(raw);
    if (!siret) return { ok: false, error: 'Indiquez le numéro SIRET de votre établissement.' };
    if (!/^\d{14}$/.test(siret)) {
        return { ok: false, error: 'Le SIRET doit contenir exactement 14 chiffres.' };
    }
    if (!isValidSiret(siret)) {
        return { ok: false, error: "Ce numéro SIRET n'est pas valide : vérifiez qu'il n'y a pas d'erreur de frappe." };
    }
    return { ok: true, value: siret };
}

/** « 12345678900012 » → « 123 456 789 00012 » (SIREN + NIC), pour l'affichage. */
export function formatSiret(siret: string): string {
    return /^\d{14}$/.test(siret)
        ? `${siret.slice(0, 3)} ${siret.slice(3, 6)} ${siret.slice(6, 9)} ${siret.slice(9)}`
        : siret;
}

// ─── Téléphone ─────────────────────────────────────────────────────────────

/** Format stocké (E.164) — miroir de firestore.rules. */
export const E164_PHONE = /^\+[1-9]\d{7,14}$/;

/**
 * Outre-mer : un numéro national (10 chiffres) dont les 4 premiers chiffres sont
 * l'un de ces préfixes appartient au plan de numérotation de l'indicatif associé.
 */
const OVERSEAS_PREFIXES: Record<string, string> = {
    '0262': '262', '0263': '262', '0269': '262', '0639': '262', '0692': '262', '0693': '262', // La Réunion, Mayotte
    '0590': '590', '0690': '590', '0691': '590', // Guadeloupe, Saint-Martin, Saint-Barthélemy
    '0594': '594', '0694': '594', // Guyane
    '0596': '596', '0696': '596', '0697': '596', // Martinique
};

/**
 * Normalise un numéro au format international E.164.
 * « 06 12 34 56 78 », « 06.12.34.56.78 », « +33 6 12 34 56 78 », « +33 (0)6… »,
 * « 0033 6… », « 0033 (0)6… » → « +33612345678 » ; « 0692 12 34 56 » → « +262692123456 » ;
 * un numéro étranger se saisit avec son indicatif (« +32… » ou « 0032… »).
 */
export function normalizePhone(raw: string): FieldCheck {
    const compact = raw.replace(/[\s  .\-()/]/g, '');
    if (!compact) return { ok: false, error: 'Indiquez un numéro de téléphone.' };

    // « 00… » → « +… » ; « +33 (0)6… » → « +336… »
    let n = compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
    if (/^\+330[1-9]\d{8}$/.test(n)) n = `+33${n.slice(4)}`;

    let phone: string | null = null;
    if (/^0[1-9]\d{8}$/.test(n)) {
        const overseas = OVERSEAS_PREFIXES[n.slice(0, 4)];
        phone = overseas ? `+${overseas}${n.slice(1)}` : `+33${n.slice(1)}`;
    } else if (n.startsWith('+33')) {
        phone = /^\+33[1-9]\d{8}$/.test(n) ? n : null;
    } else if (E164_PHONE.test(n)) {
        phone = n;
    }

    return phone
        ? { ok: true, value: phone }
        : {
            ok: false,
            error: 'Numéro de téléphone invalide. Exemple : 06 12 34 56 78. Pour un numéro étranger, commencez par « + » et l\'indicatif du pays (ex. +32 2 123 45 67).',
        };
}

/** « +33612345678 » → « 06 12 34 56 78 », « +262692123456 » → « 06 92 12 34 56 » ; autres pays : inchangé. */
export function formatPhone(phone: string): string {
    const m = /^\+(?:33|262|590|594|596)([1-9]\d{8})$/.exec(phone);
    return m ? `0${m[1]}`.replace(/(\d{2})(?=\d)/g, '$1 ') : phone;
}

// ─── Formulaire complet ────────────────────────────────────────────────────

function checkText(
    raw: string,
    limits: { min: number; max: number },
    emptyError: string,
    label: string,
): FieldCheck {
    // Espaces multiples (copier-coller) réduits à un seul, caractères invisibles retirés.
    const value = stripInvisibleChars(raw).replace(/ +/g, ' ').trim();
    if (!value) return { ok: false, error: emptyError };
    if (value.length < limits.min) {
        return { ok: false, error: `${label} doit contenir au moins ${limits.min} caractères.` };
    }
    if (value.length > limits.max) {
        return { ok: false, error: `${label} ne doit pas dépasser ${limits.max} caractères.` };
    }
    return { ok: true, value };
}

export function validateEmail(raw: string): FieldCheck {
    const email = raw.trim();
    if (!email) return { ok: false, error: 'Indiquez votre adresse e-mail.' };
    if (email.length > APPLICATION_LIMITS.email.max || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { ok: false, error: 'Adresse e-mail invalide. Exemple : jean@monrestaurant.fr' };
    }
    return { ok: true, value: email };
}

/** Motif de refus saisi par l'admin (les retours à la ligne sont conservés). */
export function validateRejectionReason(raw: string): FieldCheck {
    const reason = stripInvisibleChars(raw, { keepNewlines: true }).trim();
    const { min, max } = APPLICATION_LIMITS.rejectionReason;
    if (!reason) return { ok: false, error: 'Indiquez le motif du refus : il sera affiché au demandeur.' };
    if (reason.length < min) return { ok: false, error: `Le motif doit contenir au moins ${min} caractères.` };
    if (reason.length > max) return { ok: false, error: `Le motif ne doit pas dépasser ${max} caractères.` };
    return { ok: true, value: reason };
}

export type RestaurateurSignupValidation =
    | { ok: true; values: RestaurateurApplicationInput & { password: string } }
    | { ok: false; errors: Partial<Record<RestaurateurSignupField, string>> };

/**
 * Valide et normalise tout le formulaire ; les erreurs sont indexées par champ.
 * En mode `existingAccount`, l'e-mail et le mot de passe sont ceux du compte :
 * ils ne sont pas vérifiés ici.
 */
export function validateRestaurateurSignup(
    form: RestaurateurSignupForm,
    mode: RestaurateurSignupMode = 'signup',
): RestaurateurSignupValidation {
    const errors: Partial<Record<RestaurateurSignupField, string>> = {};
    const L = APPLICATION_LIMITS;
    const isSignup = mode === 'signup';

    const checks = {
        firstName: checkText(form.firstName, L.firstName, 'Indiquez votre prénom.', 'Le prénom'),
        lastName: checkText(form.lastName, L.lastName, 'Indiquez votre nom.', 'Le nom'),
        email: isSignup ? validateEmail(form.email) : ({ ok: true, value: form.email } as FieldCheck),
        phone: normalizePhone(form.phone),
        restaurantName: checkText(form.restaurantName, L.restaurantName, 'Indiquez le nom de votre restaurant.', 'Le nom du restaurant'),
        address: checkText(form.address, L.address, "Indiquez l'adresse de votre restaurant.", "L'adresse"),
        siret: validateSiret(form.siret),
    };
    for (const [field, check] of Object.entries(checks) as [keyof typeof checks, FieldCheck][]) {
        if (check.ok === false) errors[field] = check.error;
    }
    if (isSignup && !form.password) {
        errors.password = 'Choisissez un mot de passe.';
    } else if (isSignup && form.password.length < MIN_PASSWORD_LENGTH) {
        errors.password = `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`;
    }
    if (!form.acceptedTerms) {
        errors.acceptedTerms = 'Cochez la case pour envoyer votre demande.';
    }

    if (Object.keys(errors).length > 0) return { ok: false, errors };
    const value = (c: FieldCheck) => (c.ok ? c.value : '');
    return {
        ok: true,
        values: {
            firstName: value(checks.firstName),
            lastName: value(checks.lastName),
            email: value(checks.email),
            phone: value(checks.phone),
            restaurantName: value(checks.restaurantName),
            address: value(checks.address),
            siret: value(checks.siret),
            password: form.password,
        },
    };
}
